import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';

import { Color, ColorPickerControl } from '@iplab/ngx-color-picker';
import { Subscription } from 'rxjs';

import { TwcPopupOptions, TwcPopupRef, TwcPopupService } from '@topcon/twc-angular';

import { DbPcvizService } from '@mirage-projects/cw-pcviz-core';

import { ChromePickerSelectorComponent, ColorDefinition } from './chrome-picker-selector/chrome-picker-selector.component';
import { ColorPickerService } from './color-picker.service';

/**
 * It shows a component to select a color via numbers or using color tab
 */
@Component({
    selector: 'cw-color-picker',
    templateUrl: './color-picker.component.html',
    styleUrls: ['./color-picker.component.scss'],
    standalone: false,
})
export class ColorPickerComponent implements OnInit, OnDestroy {
    /* Component label. Can be translated */
    @Input() label: string | undefined;

    /* Indicates if the component is disabled */
    @Input() disabled = false;

    /* Indicates if the component is readonly */
    @Input() readonly = false;

    /* If this input is false, the inputs fields will not be displayed. The circle will be displayed to the left of the text*/
    @Input() showInputs = true;

    /** Color that will show in the component */
    @Input()
    set color(color: number[]) {
        color.forEach((element: number, index: number) => {
            this.listRGB[index].value = element;
        });
    }

    /** Emits an event when the color has changed, passing the color */
    @Output() colorChange = new EventEmitter<number[]>();

    listRGB: ColorDefinition[] = [
        {
            tagValue: 'R',
            value: 0,
        },
        {
            tagValue: 'G',
            value: 0,
        },
        {
            tagValue: 'B',
            value: 0,
        },
    ];

    id!: string;

    private colorControl: ColorPickerControl = new ColorPickerControl();
    private colorPropertyControl: Color = new Color();
    private valueChangesSubscription: Subscription | undefined;
    private popupRef: TwcPopupRef | undefined;

    constructor(
        private popupService: TwcPopupService,
        private colorPickerService: ColorPickerService,
        private dbPcvizService: DbPcvizService
    ) {}

    ngOnInit(): void {
        this.id = this.dbPcvizService.getUtils().generateUUID();
        this.colorControl.hideAlphaChannel();
        this.onCloseSubscription();
    }

    ngOnDestroy(): void {
        this.valueChangesSubscription?.unsubscribe();
    }

    /** @ignore */
    setComponentValue(colorValue: ColorDefinition): void {
        const index: number = ['R', 'G', 'B'].indexOf(colorValue.tagValue);
        this.listRGB[index].value = colorValue.value;

        this.colorChange.emit([this.listRGB[0].value, this.listRGB[1].value, this.listRGB[2].value]);
    }

    /** @ignore */
    getStrColor(): string {
        const colorRGB = `rgb(${this.listRGB[0].value},${this.listRGB[1].value},${this.listRGB[2].value})`;
        this.colorPropertyControl.setRgba(this.listRGB[0].value, this.listRGB[1].value, this.listRGB[2].value);

        return colorRGB;
    }

    /** @ignore */
    overlayClick(): void {
        if (this.disabled || this.readonly) {
            return;
        }
        const options: TwcPopupOptions = {
            closeByClickOutside: true,
            params: { colorData: this.getStrColor(), idColorPicker: this.id },
        };
        this.popupRef = this.popupService.open(ChromePickerSelectorComponent, options);
    }

    private onCloseSubscription(): void {
        this.valueChangesSubscription = this.colorPickerService.colorSelected$.subscribe((colorChanged) => {
            if (this.id === colorChanged.idColorPicker) {
                if (colorChanged.colorDefinition) {
                    this.setColorValue(colorChanged.colorDefinition);
                }
                this.popupRef?.close();
            }
        });
    }

    private setColorValue(colorValue: ColorDefinition[]): void {
        colorValue.forEach((color: ColorDefinition, index: number) => {
            this.listRGB[index].value = color.value;
        });

        this.colorChange.emit([this.listRGB[0].value, this.listRGB[1].value, this.listRGB[2].value]);
    }
}
